import React, { useState } from 'react';
import { useTemplates } from '@context/TemplateContext';
import { TemplateConfig } from '@/types/template.types';
import { X, Save, Tag } from 'lucide-react';
import { Button, IconButton } from '../ui/Button';
import { Input, TextArea } from '../ui/Form';

const ICON_OPTIONS = ['🎸', '🔥', '🌙', '⚡', '🎹', '🥁', '🎺', '🌊', '💀', '🌵', '🍺', '🛣️', '🎭', '👻', '🦅'];

interface SaveTemplateModalProps {
  onClose: () => void;
  getCurrentConfig: () => TemplateConfig;
}

export const SaveTemplateModal: React.FC<SaveTemplateModalProps> = ({
  onClose,
  getCurrentConfig
}) => {
  const { createTemplate, templates } = useTemplates();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('🎸');
  const [tags, setTags] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Template name is required');
      return;
    }

    if (templates.some(t => t.name.toLowerCase() === trimmedName.toLowerCase())) {
      setError('A template with this name already exists');
      return;
    }

    const tagList = tags.split(',').map(t => t.trim().toLowerCase()).filter(t => t.length > 0);
    const config = getCurrentConfig();
    createTemplate(trimmedName, description.trim(), icon, tagList, config);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSave(); }
    if (e.key === 'Escape') { onClose(); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[200]" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <header className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
          <h3 className="text-lg font-black text-slate-50 uppercase tracking-tighter flex items-center gap-2">
            💾 Save Architecture Template
          </h3>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </IconButton>
        </header>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[80vh] custom-scrollbar">
          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Select Icon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(ico => (
                <Button
                  key={ico}
                  variant="ghost"
                  size="icon"
                  className={`w-10 h-10 bg-slate-950 border-2 rounded-xl text-xl ${icon === ico ? 'border-accent bg-accent/5' : 'border-slate-800 hover:border-slate-700'}`}
                  onClick={() => setIcon(ico)}
                >
                  {ico}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Template Name *</label>
            <Input
              value={name}
              onChange={e => { setName(e.target.value); setError(null); }}
              placeholder="e.g., Southern Outlaw Ballad"
              autoFocus
              maxLength={50}
              isInvalid={!!error}
              className="bg-slate-950 border-slate-800 rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Description</label>
            <TextArea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the vibe of this configuration..."
              rows={2}
              maxLength={200}
              className="bg-slate-950 border-slate-800 rounded-xl resize-none"
            />
          </div>

          <div className="space-y-2">
            <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Tags (Comma Separated)</label>
            <Input
              value={tags}
              onChange={e => setTags(e.target.value)}
              placeholder="slow, dark, gritty..."
              leftElement={<Tag className="text-slate-400" size={14} />}
              className="bg-slate-950 border-slate-800 rounded-xl"
            />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-3">
             <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">Config Snapshot</h4>
             <ConfigPreview config={getCurrentConfig()} />
          </div>

          {error && <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold rounded-xl animate-in shake-2">{error}</div>}
        </div>

        <footer className="p-6 border-t border-slate-800 bg-slate-900/50 flex justify-end gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="text-[11px] font-black uppercase"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            leftIcon={<Save size={14} />}
            className="text-[11px] font-black uppercase shadow-lg shadow-amber-500/10"
          >
            Save Template
          </Button>
        </footer>
      </div>
    </div>
  );
};

const ConfigPreview: React.FC<{ config: TemplateConfig }> = ({ config }) => {
  return (
    <div className="grid grid-cols-1 gap-2 text-[11px] font-bold uppercase tracking-tight">
      <div className="flex items-center gap-2"><span className="text-slate-700 w-16">Vocals:</span> <span className="text-slate-400">{config.vocalConfig.mode === 'blend' ? 'Custom Blend' : config.vocalConfig.singlePackKey || 'None'}</span></div>
      <div className="flex items-center gap-2"><span className="text-slate-700 w-16">Genres:</span> <span className="text-slate-400 truncate">{config.musicalBlend.length > 0 ? config.musicalBlend.join(', ') : 'None'}</span></div>
      <div className="flex items-center gap-2"><span className="text-slate-700 w-16">Moods:</span> <span className="text-slate-400 truncate">{config.moodArchitecture.length > 0 ? config.moodArchitecture.join(', ') : 'None'}</span></div>
      {config.production && (
        <div className="flex items-center gap-2"><span className="text-slate-700 w-16">Prod:</span> <span className="text-slate-400">{config.production.selectedKey ? `${config.production.selectedKey.root} ${config.production.selectedKey.mode}` : 'Auto'} @ {config.production.selectedTempo} BPM</span></div>
      )}
    </div>
  );
};
